import React, { useState } from "react";
import { useI18n } from "../i18n/I18nContext";
import { CreditCard, Landmark, MapPin, ExternalLink, Copy, Check } from "lucide-react";

const STRIPE_LINK = "https://buy.stripe.com/dRmeVeeXU1A895U4g24F202";

/**
 * Reusable multi-region payment widget — Stripe card link on top, regional
 * transfer cards (USA Zelle/CashApp, Canada Interac, Haiti Unibank, DR, Chile, PayPal)
 * driven by the same `payInfo.regions` i18n data used on /payment-info.
 *
 * Used on /payment-info, /fomasyon-web, /programation-ai.
 */
export default function PaymentRegionsBlock({ idPrefix = "pay" }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState("");
  const REGIONS = t("payInfo.regions") || [];

  const copy = async (val) => {
    try {
      await navigator.clipboard.writeText(val);
      setCopied(val);
      setTimeout(() => setCopied(""), 1500);
    } catch {}
  };

  return (
    <div data-testid={`${idPrefix}-regions-block`}>
      {/* Stripe */}
      <div className="card-imperial p-6 md:p-8 mb-10" data-testid={`${idPrefix}-stripe-card`}>
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className="h-14 w-14 bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center flex-shrink-0">
            <CreditCard size={24} className="text-[#d4af37]" />
          </div>
          <div className="flex-1">
            <div className="overline">{t("payInfo.stripeOverline")}</div>
            <div className="font-serif text-xl md:text-2xl mt-1">{t("payInfo.stripeTitle")}</div>
            <div className="text-sm text-neutral-400 mt-1">{t("payInfo.stripeNote")}</div>
          </div>
          <a
            href={STRIPE_LINK}
            target="_blank"
            rel="noreferrer"
            className="btn-imperial"
            data-testid={`${idPrefix}-stripe-link`}
          >
            {t("payInfo.payCard")} <ExternalLink size={14} />
          </a>
        </div>
      </div>

      {/* Regional */}
      <div className="mb-6">
        <div className="overline mb-3">{t("payInfo.manualOverline")}</div>
        <h2 className="font-serif text-2xl md:text-3xl">{t("payInfo.manualTitle")}</h2>
        <p className="mt-2 text-neutral-500 text-sm max-w-2xl">{t("payInfo.manualSubtitle")}</p>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {REGIONS.map((r) => {
          const key = r.key || r.region;
          const slug = (r.key || r.region.split(" ")[0]).toLowerCase();
          return (
            <div key={key} className="card-imperial p-6" data-testid={`${idPrefix}-region-${slug}`}>
              <div className="flex items-start gap-3 mb-4">
                <div className="h-10 w-10 bg-[#e6192b]/10 border border-[#e6192b]/30 flex items-center justify-center flex-shrink-0">
                  <MapPin size={18} className="text-[#e6192b]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-condensed text-xs tracking-widest uppercase text-[#d4af37]">{r.region}</div>
                  <div className="font-serif text-lg md:text-xl mt-0.5">{r.recipient || r.name}</div>
                </div>
              </div>
              <ul className="space-y-2.5">
                {r.lines.map((l) => (
                  <li key={l.label} className="flex items-center justify-between gap-3 border-b border-[#141414] pb-2 last:border-0">
                    <div className="min-w-0">
                      <div className="text-xs text-neutral-500">{l.label}</div>
                      <div className="font-condensed text-sm text-white tracking-wide truncate">{l.value}</div>
                    </div>
                    <button
                      onClick={() => copy(l.value)}
                      className="text-neutral-500 hover:text-[#d4af37] transition-colors flex-shrink-0"
                      data-testid={`${idPrefix}-copy-${l.value.slice(0, 8).replace(/\s/g, "")}`}
                      aria-label={`Copy ${l.label}`}
                    >
                      {copied === l.value ? <Check size={14} className="text-[#d4af37]" /> : <Copy size={14} />}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <div className="mt-10 card-imperial p-6 border-[#d4af37]/30 bg-[#d4af37]/5">
        <div className="flex items-start gap-3">
          <Landmark size={18} className="text-[#d4af37] mt-0.5 flex-shrink-0" />
          <div className="text-sm text-neutral-300">
            <div className="font-condensed tracking-widest uppercase text-xs text-[#d4af37] mb-1">{t("payInfo.noticeLabel")}</div>
            {t("payInfo.notice")}
          </div>
        </div>
      </div>
    </div>
  );
}
